import { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/assist/ui/button';
import type { ActivePlatformConsent, ConsentLanguage, PlatformConsentType } from '../../types';
import { CONSENT_TYPE_LABELS, CONSENT_TYPE_SLUGS } from '../../types';
import { getActiveConsents, createMembershipConsent, declineConsent, updateProfile } from '../../api/client';
import i18n from '../../i18n/config';

const LANGUAGE_LABELS: Record<ConsentLanguage, string> = {
  en: 'English',
  nl: 'Nederlands',
  fr: 'Français',
};

const VALID_LANGUAGES: ConsentLanguage[] = ['en', 'nl', 'fr'];

const DOMPURIFY_CONFIG = {
  ALLOWED_TAGS: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'br', 'ul', 'ol', 'li', 'a', 'strong', 'em', 'b', 'i', 's', 'u', 'blockquote', 'hr', 'span', 'div'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
};

interface MembershipConsentProps {
  activeConsentVersion: string | null;
  preferredLanguage: string | null;
  onComplete: () => void;
}

export const MembershipConsent = ({ activeConsentVersion: _activeConsentVersion, preferredLanguage, onComplete }: MembershipConsentProps) => {
  const { t } = useTranslation('consent');
  const [consents, setConsents] = useState<ActivePlatformConsent[]>([]);

  const getInitialLanguage = (): ConsentLanguage => {
    if (preferredLanguage && VALID_LANGUAGES.includes(preferredLanguage as ConsentLanguage)) {
      return preferredLanguage as ConsentLanguage;
    }
    return 'en';
  };

  const [selectedLanguage, setSelectedLanguage] = useState<ConsentLanguage>(getInitialLanguage());
  const [acceptedConsents, setAcceptedConsents] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allAccepted = consents.length > 0 && consents.every(c => acceptedConsents[c.consentType]);

  useEffect(() => {
    const targetLang = preferredLanguage && VALID_LANGUAGES.includes(preferredLanguage as ConsentLanguage)
      ? preferredLanguage
      : 'en';
    if (i18n.language !== targetLang) {
      i18n.changeLanguage(targetLang);
    }
  }, [preferredLanguage]);

  const handleLanguageChange = useCallback((lang: ConsentLanguage) => {
    setSelectedLanguage(lang);

    i18n.changeLanguage(lang);

    updateProfile({ preferred_language: lang }).catch((err) => {
      console.warn('Failed to save language preference:', err);
    });
  }, []);

  useEffect(() => {
    const fetchConsents = async () => {
      try {
        const data = await getActiveConsents();

        const sorted = [...data].sort((a, b) => {
          if (a.displayMode === 'full_content' && b.displayMode !== 'full_content') return -1;
          if (a.displayMode !== 'full_content' && b.displayMode === 'full_content') return 1;
          return 0;
        });

        setConsents(sorted);

        const initialAccepted: Record<string, boolean> = {};
        sorted.forEach(c => {
          initialAccepted[c.consentType] = false;
        });
        setAcceptedConsents(initialAccepted);

        const firstConsent = sorted[0];
        if (firstConsent?.texts && !firstConsent.texts[selectedLanguage]) {
          const availableLanguages = Object.keys(firstConsent.texts) as ConsentLanguage[];
          if (availableLanguages.includes('en')) {
            setSelectedLanguage('en');
          } else if (availableLanguages.length > 0) {
            setSelectedLanguage(availableLanguages[0]);
          }
        }
      } catch (err) {
        console.error('Failed to fetch consents:', err);
        setError(t('errors.loadFailed'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchConsents();
  }, [selectedLanguage, t]);

  const handleToggleConsent = useCallback((consentType: string) => {
    setAcceptedConsents(prev => ({
      ...prev,
      [consentType]: !prev[consentType],
    }));
  }, []);

  const handleViewDocument = useCallback((consentType: PlatformConsentType) => {
    const slug = CONSENT_TYPE_SLUGS[consentType];
    const url = `/legal/${slug}?lang=${selectedLanguage}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [selectedLanguage]);

  const handleAccept = useCallback(async () => {
    if (!allAccepted || isSubmitting || consents.length === 0) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await Promise.all(
        consents.map(consent =>
          createMembershipConsent({
            consent_type: consent.consentType,
            consent_version: consent.version,
          })
        )
      );

      onComplete();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('errors.saveFailed');
      setError(errorMessage);
      setIsSubmitting(false);
    }
  }, [allAccepted, isSubmitting, consents, onComplete, t]);

  const handleDecline = useCallback(async () => {
    if (isSubmitting || consents.length === 0) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await Promise.all(
        consents.map(consent =>
          declineConsent({
            consent_type: consent.consentType,
            consent_version: consent.version,
          })
        )
      );

      onComplete();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('errors.saveFailed');
      setError(errorMessage);
      setIsSubmitting(false);
    }
  }, [isSubmitting, consents, onComplete, t]);

  const sanitizeHtml = useCallback((html: string): string => {
    return DOMPurify.sanitize(html, DOMPURIFY_CONFIG);
  }, []);

  if (isLoading) {
    return (
      <div className="standalone-page flex justify-center items-center min-h-[400px] text-muted-foreground text-base">
        {t('loading')}
      </div>
    );
  }

  if (consents.length === 0) {
    return (
      <div className="standalone-page max-w-[800px] mx-auto py-10 px-5">
        <h1 className="font-display text-4xl font-extrabold tracking-[-0.015em] text-foreground mb-4">{t('noConsent.title')}</h1>
        <div className="bg-status-attention/15 border border-status-attention rounded-lg p-6 text-center">
          <p className="text-status-critical text-sm">
            {t('noConsent.message')}
          </p>
        </div>
      </div>
    );
  }

  const fullContentConsents = consents.filter(c => c.displayMode === 'full_content');
  const linkOnlyConsents = consents.filter(c => c.displayMode === 'link_only');

  const availableLanguages = consents[0]?.texts
    ? (Object.keys(consents[0].texts) as ConsentLanguage[])
    : ['en'] as ConsentLanguage[];

  return (
    <div className="standalone-page max-w-[800px] mx-auto py-10 px-5">
      <h1 className="font-display text-4xl font-extrabold tracking-[-0.015em] text-foreground mb-4">
        {t('title')}
      </h1>
      <p className="text-base text-muted-foreground mb-6">
        {t('subtitle')}
      </p>

      {error && (
        <div className="bg-destructive/10 border border-destructive rounded-lg p-4 mb-6">
          <p className="text-destructive text-sm">{error}</p>
        </div>
      )}

      {availableLanguages.length > 1 && (
        <div className="flex gap-2 mb-6">
          {availableLanguages.map((lang) => (
            <button
              key={lang}
              onClick={() => handleLanguageChange(lang)}
              className={
                selectedLanguage === lang
                  ? 'px-3 py-1.5 text-sm font-medium rounded-md bg-primary text-primary-foreground transition-colors'
                  : 'px-3 py-1.5 text-sm font-medium rounded-md bg-card text-foreground hover:bg-muted transition-colors'
              }
            >
              {LANGUAGE_LABELS[lang]}
            </button>
          ))}
        </div>
      )}

      {fullContentConsents.map((consent) => {
        const currentText = consent.texts[selectedLanguage];
        if (!currentText) return null;

        const sanitizedHtml = sanitizeHtml(currentText.fullText);

        return (
          <div key={consent.consentType}>
            <div className="bg-card border border-border rounded-xl mb-6 overflow-hidden">
              <div className="bg-muted px-6 py-4 border-b border-border">
                <h2 className="text-lg font-semibold text-foreground m-0">
                  {currentText.title}
                  <span className="inline-block px-2 py-0.5 bg-border rounded text-xs text-muted-foreground ml-2">v{consent.version}</span>
                </h2>
                <p className="text-sm text-muted-foreground mt-2 m-0">{currentText.summary}</p>
              </div>
              <div className="p-6 max-h-[400px] overflow-y-auto">
                <div
                  className="text-sm leading-7 text-foreground/80"
                  dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                />
              </div>
            </div>

            <div className="flex items-start gap-3 p-5 bg-muted rounded-xl mb-4">
              <input
                type="checkbox"
                id={`consent-${consent.consentType}`}
                checked={acceptedConsents[consent.consentType] || false}
                disabled={isSubmitting}
                onChange={() => handleToggleConsent(consent.consentType)}
                className="w-5 h-5 cursor-pointer accent-primary mt-0.5 shrink-0"
              />
              <label htmlFor={`consent-${consent.consentType}`} className="text-sm text-foreground/80 cursor-pointer leading-relaxed flex-1">
                {t('checkbox.labelDataProcessing', { defaultValue: t('checkbox.label') })}
              </label>
            </div>
          </div>
        );
      })}

      {linkOnlyConsents.length > 0 && (
        <>
          {fullContentConsents.length > 0 && <div className="h-px bg-border my-6" />}

          <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">{t('sections.legalDocuments', { defaultValue: 'Legal Documents' })}</p>

          {linkOnlyConsents.map((consent) => {
            const currentText = consent.texts[selectedLanguage];
            const label = CONSENT_TYPE_LABELS[consent.consentType as PlatformConsentType] || consent.consentType;

            return (
              <div key={consent.consentType} className="flex items-center justify-between px-5 py-4 bg-muted rounded-xl mb-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id={`consent-${consent.consentType}`}
                    checked={acceptedConsents[consent.consentType] || false}
                    disabled={isSubmitting}
                    onChange={() => handleToggleConsent(consent.consentType)}
                    className="w-5 h-5 cursor-pointer accent-primary mt-0.5 shrink-0"
                  />
                  <label htmlFor={`consent-${consent.consentType}`} className="text-sm text-foreground/80 cursor-pointer leading-relaxed flex-1">
                    {t(`checkbox.label${consent.consentType === 'privacy_policy' ? 'PrivacyPolicy' : 'TermsOfUse'}`, {
                      defaultValue: `I accept the ${label}`,
                    })}
                    {currentText && (
                      <span className="inline-block px-2 py-0.5 bg-border rounded text-xs text-muted-foreground ml-2">v{consent.version}</span>
                    )}
                  </label>
                </div>
                <Button
                  type="button"
                  onClick={() => handleViewDocument(consent.consentType as PlatformConsentType)}
                  variant="link"
                  size="sm"
                >
                  {t('buttons.view', { defaultValue: 'View' })} →
                </Button>
              </div>
            );
          })}
        </>
      )}

      <div className="flex flex-col items-center gap-4 mt-8">
        <div className="flex gap-4 flex-wrap justify-center">
          <Button
            onClick={handleDecline}
            disabled={isSubmitting}
            variant="outline"
            size="lg"
          >
            {t('buttons.decline')}
          </Button>
          <Button
            onClick={handleAccept}
            disabled={!allAccepted || isSubmitting}
            size="lg"
          >
            {isSubmitting ? t('buttons.saving') : t('buttons.acceptAll', { defaultValue: t('buttons.accept') })}
          </Button>
        </div>
      </div>
    </div>
  );
};
